import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Card,
  Statistic,
  Row,
  Col,
  Spin,
  Alert,
  Divider,
  Tabs,
  Tag,
  Empty,
  DatePicker,
  Button,
  Space,
  Form,
} from "antd";
import {
  PhoneFilled,
  MessageFilled,
  StarFilled,
  TeamOutlined,
  CloseOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

function CallerStats() {

  const { name } = useParams();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [currentDate, setCurrentDate] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callerExists, setCallerExists] = useState(null);

  const checkCallerExists = async () => {

    try {

      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/callers`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      const callerExists = response.data.some(caller => caller.name === name);
      setCallerExists(callerExists);
      
      if (!callerExists) {
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }

    } catch (error) {
      console.error('Error checking caller existence:', error);
      setCallerExists(false);
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    }

  };

   useEffect(() => {
      checkCallerExists();
    }, [name]);

    useEffect(() => {
      if (callerExists === true) {
        fetchData();
      }
    }, [callerExists]);

    if (callerExists === null) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column'
        }}>
          <Spin size="large" style={{ color: '#f49b33' }} />
          <p style={{ color: '#fff', marginTop: '16px' }}>Verificando caller...</p>
        </div>
      );
    }

    if (callerExists === false) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh', 
          flexDirection: 'column' 
        }}> 
          <Alert 
            message={<span style={{ color: '#fff' }}>Caller no encontrado</span>}
            description={<span style={{ color: '#fff' }}>{`El caller "${name}" no existe o no está activo. Redirigiendo al dashboard...`}</span>}
            type="warning" 
            showIcon 
            style={{ 
              backgroundColor: '#353435', 
              borderColor: '#f49b33'
            }} 
          /> 
        </div>
      );
    }

    const formatSecondsToHMS = (totalSeconds) => {

      const seconds = Number(totalSeconds);

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:${String(secs).padStart(2, "0")}`;

    };

    const fetchData = async (startDate = "", endDate = "") => {

    try {

      setLoading(true);

      const authToken = localStorage.getItem('authToken');
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      setCurrentDate(formattedDate);

      const params = {
        caller: name,
        sender: name
      };

      if (startDate && endDate) {

        params.startDate = startDate;
        params.endDate = endDate;

      }

      const [callsRes, messagesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/stats`, {
          headers: { Authorization: `Bearer ${authToken}` },
          params: params
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/sms-stats`, {
          headers: { Authorization: `Bearer ${authToken}` },
          params: params
        })
      ]);

      const callMap = { inbound: {}, outbound: {} };
      const messageMap = {};
      let totalCallSeconds = { inbound: 0, outbound: 0 };

      callsRes.data
        .forEach((row) => {
          const hour = Math.floor(row.slot / 2);
          const min = row.slot % 2 === 0 ? "00" : "30";
          const timeLabel = `${String(hour).padStart(2, "0")}:${min}`;
          const direction = row.direction_type || "outbound";

          if (!callMap[direction][timeLabel]) {
            callMap[direction][timeLabel] = 0;
          }
          callMap[direction][timeLabel] += row.total_calls;

          const callSeconds = parseInt(row.total_call_seconds, 10) || 0;
          totalCallSeconds[direction] += callSeconds;
          
        });

      messagesRes.data
        .forEach((row) => {
          const hour = Math.floor(row.slot / 2);
          const min = row.slot % 2 === 0 ? "00" : "30";
          const timeLabel = `${String(hour).padStart(2, "0")}:${min}`;
          messageMap[timeLabel] =
            (messageMap[timeLabel] || 0) + row.total_messages;
        });

      const allSlots = [];

      for (let h = 0; h < 24; h++) {

        allSlots.push(`${String(h).padStart(2, "0")}:00`);
        allSlots.push(`${String(h).padStart(2, "0")}:30`);

      }

      const timeSeries = allSlots.map((time) => ({
        time,
        callsInbound: callMap.inbound[time] || 0,
        callsOutbound: callMap.outbound[time] || 0,
        messages: messageMap[time] || 0,
        hour: parseInt(time.split(":")[0]),
      }));

      const callsInbound = Object.values(callMap.inbound).reduce(
        (a, b) => a + b,
        0
      );
      const callsOutbound = Object.values(callMap.outbound).reduce(
        (a, b) => a + b,
        0
      );
      const messages = Object.values(messageMap).reduce((a, b) => a + b, 0);

      let peakHour = {
        inbound: { time: "", calls: 0 },
        outbound: { time: "", calls: 0 },
      };

      Object.entries(callMap.inbound).forEach(([time, count]) => {
        if (count > peakHour.inbound.calls) {
          peakHour.inbound = { time, calls: count };
        }
      });

      Object.entries(callMap.outbound).forEach(([time, count]) => {
        if (count > peakHour.outbound.calls) {
          peakHour.outbound = { time, calls: count };
        }
      });

      setData(timeSeries);
      setStats({
        calls: callsInbound + callsOutbound,
        callsInbound,
        callsOutbound,
        messages,
        peakHour,
        totalCallTime: {
          inbound: formatSecondsToHMS(totalCallSeconds.inbound),
          outbound: formatSecondsToHMS(totalCallSeconds.outbound),
          combined: formatSecondsToHMS(
            totalCallSeconds.inbound + totalCallSeconds.outbound
          ),
        },
        lastActivity:
          startDate && endDate
            ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
            : formattedDate,
      });

    } catch (err) {

      console.error("Error fetching data:", err);
      setError("Error al cargar los datos del agente");

    } finally {

      setLoading(false);

    }

  };

  const formatDisplayDate = (dateString) => {

    const [year, month, day] = dateString.split("T")[0].split("-");
    const fixedDate = new Date(year, month - 1, day);

    return fixedDate.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  };

  const handleDateFilter = (dates) => {

    if (dates && dates.length === 2) {

      const [start, end] = dates;
      const startDate = start.format("YYYY-MM-DD");
      const endDate = end.format("YYYY-MM-DD");
      setDateRange(dates);
      fetchData(startDate, endDate);

    } else {

      setDateRange([]);
      fetchData();

    }

  };

  const hourPerformance = data.reduce((acc, item) => {

    const hour = item.hour;

    if (!acc[hour]) {

      acc[hour] = { callsInbound: 0, callsOutbound: 0, messages: 0 };

    }
    
    acc[hour].callsInbound += item.callsInbound;
    acc[hour].callsOutbound += item.callsOutbound;
    acc[hour].messages += item.messages;
    return acc;

  }, {});

  const hourPerformanceArray = Object.keys(hourPerformance).map((hour) => ({
    hour: `${hour}:00`,
    callsInbound: hourPerformance[hour].callsInbound,
    callsOutbound: hourPerformance[hour].callsOutbound,
    messages: hourPerformance[hour].messages,
  }));

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#2a2b2b",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" tip="Cargando datos..." />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "#2a2b2b",
          minHeight: "100vh",
          padding: "20px",
        }}
      >
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: "800px", margin: "40px auto" }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#2a2b2b",
        minHeight: "100vh",
        padding: "20px",
        color: "white",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <h1
              style={{
                color: "#f49b33",
                margin: 0,
                fontSize: "28px",
              }}
            >
              {name}
            </h1>
            <p
              style={{
                color: "#aaa",
                margin: "4px 0 0",
                fontSize: "14px",
              }}
            >
              {typeof stats.lastActivity === "string" &&
              !stats.lastActivity.trim().includes("de")
                ? "Datos del " + formatDisplayDate(currentDate)
                : "Datos del " + stats.lastActivity}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Form
              form={form}
              initialValues={{ dateRange: [] }}
              style={{ margin: 0 }}
            >
              <Space direction="horizontal" size={12} wrap>
                <Form.Item name="dateRange" style={{ margin: 0 }} noStyle>
                  <RangePicker
                    onChange={handleDateFilter}
                    style={{ width: "250px" }}
                    format="YYYY-MM-DD"
                    placeholder={["Fecha inicio", "Fecha fin"]}
                    suffixIcon={
                      <CalendarOutlined style={{ color: "#f49b33" }} />
                    }
                    popupStyle={{ backgroundColor: "#2a2b2b", color: "#fff" }}
                  />
                </Form.Item>
                {dateRange.length > 0 && (
                  <Button
                    onClick={() => {
                      form.resetFields();
                      handleDateFilter(null);
                    }}
                    icon={<CloseOutlined />}
                    style={{
                      color: "#f49b33",
                      borderColor: "#f49b33",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                )}
              </Space>
            </Form>

            <div style={{ display: "flex", gap: "16px" }}>
              <Tag icon={<TeamOutlined />} color="blue">
                Agente
              </Tag>
              {stats.peakHour.inbound.calls > 0 && (
                <Tag icon={<StarFilled />} color="green">
                  Hora pico (Inbound): {stats.peakHour.inbound.time} (
                  {stats.peakHour.inbound.calls})
                </Tag>
              )}
              {stats.peakHour.outbound.calls > 0 && (
                <Tag icon={<StarFilled />} color="orange">
                  Hora pico (Outbound): {stats.peakHour.outbound.time} (
                  {stats.peakHour.outbound.calls})
                </Tag>
              )}
            </div>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                backgroundColor: "#353435",
                borderRadius: "8px",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "#aaa" }}>Llamadas Entrantes</span>
                }
                value={stats.callsInbound}
                prefix={<PhoneFilled style={{ color: "#4CAF50" }} />}
                valueStyle={{ color: "#fff" }}
              />
              <div style={{ color: "#aaa", fontSize: "12px" }}>
                {stats.peakHour.inbound.calls > 0
                  ? `Hora pico: ${stats.peakHour.inbound.time}`
                  : "Sin llamadas entrantes"}
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                backgroundColor: "#353435",
                borderRadius: "8px",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "#aaa" }}>Llamadas Salientes</span>
                }
                value={stats.callsOutbound}
                prefix={<PhoneFilled style={{ color: "#f49b33" }} />}
                valueStyle={{ color: "#fff" }}
              />
              <div style={{ color: "#aaa", fontSize: "12px" }}>
                {stats.peakHour.outbound.calls > 0
                  ? `Hora pico: ${stats.peakHour.outbound.time}`
                  : "Sin llamadas salientes"}
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                backgroundColor: "#353435",
                borderRadius: "8px",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "#aaa" }}>Tiempo en Llamadas</span>
                }
                value={stats.totalCallTime.combined}
                prefix={<PhoneFilled style={{ color: "#8884d8" }} />}
                valueStyle={{ color: "#fff", fontFamily: "monospace" }}
              />
              <div style={{ color: "#aaa", fontSize: "12px" }}>
                {`Entr: ${stats.totalCallTime.inbound} | Sal: ${stats.totalCallTime.outbound}`}
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                backgroundColor: "#353435",
                borderRadius: "8px",
              }}
            >
              <Statistic
                title={<span style={{ color: "#aaa" }}>Mensajes enviados</span>}
                value={stats.messages}
                prefix={<MessageFilled style={{ color: "#00c49f" }} />}
                valueStyle={{ color: "#fff" }}
              />
              <div style={{ color: "#aaa", fontSize: "12px" }}>
                {stats.messages > 0
                  ? "Activo en mensajería"
                  : "Sin mensajes enviados"}
              </div>
            </Card>
          </Col>
        </Row>

        <Divider style={{ borderColor: "#444" }} />

        <Tabs defaultActiveKey="1" centered>
          <TabPane
            tab={
              <span style={{ color: "#aaa", fontWeight: "bold" }}>
                Actividad por Intervalos
              </span>
            }
            key="1"
          >
            <div style={{ marginBottom: "24px" }}>
              <div style={{ height: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#4a4a4a" />
                    <XAxis
                      dataKey="time"
                      tick={{ fill: "#ccc" }}
                      interval={Math.floor(data.length / 12)}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#00c49f"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#353435",
                        borderColor: "#f49b33",
                        borderRadius: "4px",
                      }}
                      labelStyle={{ color: "#aaa" }}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="callsInbound"
                      name="Llamadas Entrantes"
                      fill="#4CAF50"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="callsOutbound"
                      name="Llamadas Salientes"
                      fill="#f49b33"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="messages"
                      name="Mensajes"
                      stroke="#00c49f"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <h3 style={{ color: "#f49b33" }}>Resumen por Hora</h3>
                <div style={{ height: "300px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourPerformanceArray}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#4a4a4a" />
                      <XAxis dataKey="hour" tick={{ fill: "#ccc" }} />
                      <YAxis tick={{ fill: "#ccc" }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#353435",
                          borderColor: "#f49b33",
                        }}
                        labelStyle={{ color: "#aaa" }}
                      />
                      <Legend />
                      <Bar
                        dataKey="callsInbound"
                        name="Llamadas Entrantes"
                        fill="#4CAF50"
                      />
                      <Bar
                        dataKey="callsOutbound"
                        name="Llamadas Salientes"
                        fill="#f49b33"
                      />
                      <Bar dataKey="messages" name="Mensajes" fill="#00c49f" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <h3 style={{ color: "#f49b33" }}>Distribución de Llamadas</h3>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Entrantes",
                            value: stats.callsInbound,
                            color: "#4CAF50",
                          },
                          {
                            name: "Salientes",
                            value: stats.callsOutbound,
                            color: "#f49b33",
                          },
                          {
                            name: "Mensajes",
                            value: stats.messages,
                            color: "#00c49f",
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        <Cell fill="#4CAF50" />
                        <Cell fill="#f49b33" />
                        <Cell fill="#00c49f" />
                      </Pie>
                      <Legend />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0];
                            return (
                              <div
                                style={{
                                  backgroundColor: "#353435",
                                  border: "1px solid #f49b33",
                                  borderRadius: "4px",
                                  padding: "8px",
                                  color: "#aaa",
                                }}
                              >
                                <p style={{ margin: 0 }}>
                                  {data.name}: {data.value}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span style={{ color: "#aaa", fontWeight: "bold" }}>
                Detalles
              </span>
            }
            key="2"
          >
            <div
              style={{
                height: "400px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#aaa",
              }}
            >
              <Empty
                description={
                  <span style={{ color: "#aaa", fontWeight: "bold" }}>
                    No hay más datos disponibles
                  </span>
                }
              />
            </div>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
}

export default CallerStats;
